import React, { Fragment, useMemo, useState, useCallback, useEffect } from 'react';
import { Button, Modal, ModalHeader, ModalBody, ModalFooter } from 'reactstrap';
import { toast } from 'react-toastify';
import { sortBy, groupBy, chunk, keyBy, uniq } from 'lodash';
import { useToggle } from 'react-use';
import jsbarcode from 'jsbarcode';
import { jsPDF } from 'jspdf';
import { format as formatDate } from 'date-fns';

import { getFirestore, collection, query, where, getCountFromServer } from 'firebase/firestore';

import env from '../../env';
import firebase, { functions } from '../../firebase';
import { getDocumentsData, batch } from '../../shared/firebase';
import { idWithTenant } from '../../shared/util';
import AdminPage from '../hocs/AdminPage';
import ModelFormModal from '../modals/ModelFormModal';
import LocationsBatchAddModal from '../modals/LocationsBatchAddModal';
import LocationsBatchEditModal from '../modals/LocationsBatchEditModal';
import useCollectionSubscription from '../hooks/useCollectionSubscription';
import useQueryParams from '../hooks/useQueryParams';
import QueryInput from '../QueryInput';
import CursorPagination from '../CursorPagination';
import AddButton from '../AddButton';
import EditButton from '../EditButton';
import DeleteButton from '../DeleteButton';
import ExportButton from '../ExportButton';
import { fields } from '../../shared/models/location';

const { keys } = Object;
const printBarcode = functions.httpsCallable('printBarcode');
const db = firebase.firestore();
const locationsRef = db.collection('locations');
const inventoriesRef = db.collection('inventories');

const countPerPage = 100;

export default AdminPage(function AdminLocations(props) {
  const queryParams = useQueryParams();
  const { key: keyFilter = '' } = queryParams;
  const keyFilterTrimmed = keyFilter.trim();

  const [cursors, setCursors] = useState([]);
  const currentCursor = cursors[cursors.length - 1] || null;

  useEffect(() => setCursors([]), [keyFilterTrimmed]);

  const [totalCount, setTotalCount] = useState(null);
  useEffect(() => {
    setTotalCount(null);
    let cancelled = false;
    const modularDb = getFirestore();
    const modularLocationsRef = collection(modularDb, 'locations');
    const q = keyFilterTrimmed
      ? query(
          modularLocationsRef,
          where('sortKey', '>=', keyFilterTrimmed),
          where('sortKey', '<', keyFilterTrimmed + '')
        )
      : modularLocationsRef;
    getCountFromServer(q).then((snap) => {
      if (!cancelled) setTotalCount(snap.data().count);
    }).catch(_ => _);
    return () => { cancelled = true; };
  }, [keyFilterTrimmed]);

  const baseRef = useMemo(() => {
    const ref = keyFilterTrimmed
      ? locationsRef
          .where('sortKey', '>=', keyFilterTrimmed)
          .where('sortKey', '<', keyFilterTrimmed + '')
          .orderBy('sortKey')
      : locationsRef.orderBy('sortKey');
    return ref.limit(countPerPage + 1);
  }, [keyFilterTrimmed]);
  const pageRef = useMemo(() => currentCursor ? baseRef.startAfter(currentCursor) : baseRef, [baseRef, currentCursor]);
  const { items: rawLocations, isLoading } = useCollectionSubscription(pageRef, [keyFilterTrimmed, currentCursor], { detail: true, initialItems: null });

  const hasNext = (rawLocations?.length || 0) > countPerPage;
  const locations = hasNext ? rawLocations.slice(0, countPerPage) : (rawLocations || []);
  const hasPrev = cursors.length > 0;
  const onNext = useCallback(() => {
    if (!hasNext || locations.length === 0) return;
    setCursors(_ => [..._, locations[locations.length - 1].sortKey]);
  }, [hasNext, locations]);
  const onPrev = useCallback(() => setCursors(_ => _.slice(0, -1)), []);
  const onFirst = useCallback(() => setCursors([]), []);

  const [showsBatchAddModal, toggleBatchAddModal] = useToggle(false);
  const [showsBatchEditModal, toggleBatchEditModal] = useToggle(false);

  const rowsForExport = () => {
    return locations.map(_ => ({ sortKey: _.sortKey, name: _.name }));
  };

  const fetchTargetLocationsForCsv = async () => {
    if (!keyFilterTrimmed) {
      return locationsRef.orderBy('sortKey').get().then(({ docs }) => docs.map(_ => ({ id: _.id, ..._.data() })));
    }
    const { docs } = await locationsRef
      .where('sortKey', '>=', keyFilterTrimmed)
      .where('sortKey', '<', keyFilterTrimmed + '')
      .orderBy('sortKey')
      .get();
    return docs.map(_ => ({ id: _.id, ..._.data() }));
  };

  const rowsForInventoryCsv = async () => {
    const targetLocations = await fetchTargetLocationsForCsv();
    if (targetLocations.length === 0) return [];
    const locationsById = keyBy(targetLocations, 'id');
    const locationIdChunks = chunk(targetLocations.map(_ => _.id), 10);
    const results = await Promise.all(
      locationIdChunks.map(_ =>
        inventoriesRef.where('locationId', 'in', _).where('status', '==', 'shelved').get()
      )
    );
    const inventories = results.flatMap(_ => _.docs.map(d => ({ id: d.id, ...d.data() })));
    const productKeys = uniq(inventories.map(_ => [_.tenantId, _.productId].join('-')));
    const productRefs = productKeys.map((_) => {
      const [tenantId, productId] = _.split('-');
      return db.collection('tenants').doc(tenantId).collection('products').doc(productId);
    });
    const productChunks = chunk(productRefs, 1000);
    const products = (await productChunks.reduce(async (x, refs) => [...await x, ...await getDocumentsData(refs)], Promise.resolve([]))).filter(_ => _.exists);
    const productsById = keyBy(products, _ => idWithTenant(_.ref));
    return sortBy(inventories, _ => locationsById[_.locationId]?.sortKey, 'productId').map((inv) => {
      const {
        id, tenantId, receivingRequestId, receivingRequestDocumentNumber, productId,
        isNg, productBarcodeInfo, locationId, productBarcodeInfoType, receivingRequestInspectType,
        createdAt, status, shelvedAt, pickedAt, shippingRequestId, receivingRequestCheckNote,
        combiningRequestId, locationKey, firstShelvedAt, expirationDate, wasNoFnsku,
      } = inv;
      const product = productsById[[tenantId, productId].join('-')];
      const loc = locationsById[locationId];
      return {
        id,
        tenantId,
        receivingRequestId,
        receivingRequestDocumentNumber,
        receivingRequestInspectType,
        receivingRequestCheckNote,
        combiningRequestId,
        productId,
        sku: product?.sku,
        isAmazonProduct: product?.isAmazonProduct,
        isCase: product?.isCase,
        isNg,
        wasNoFnsku,
        productBarcodeInfo,
        productBarcodeInfoType,
        locationId,
        locationKey: loc?.sortKey || locationKey,
        locationName: loc?.name || '',
        status,
        expirationDate: expirationDate?.toDate ? formatDate(expirationDate.toDate(), 'yyyy/MM/dd') : '',
        shelvedAt: shelvedAt?.toDate ? formatDate(shelvedAt.toDate(), 'yyyy/MM/dd HH:mm:ss') : '',
        firstShelvedAt: firstShelvedAt?.toDate ? formatDate(firstShelvedAt.toDate(), 'yyyy/MM/dd HH:mm:ss') : '',
        shippingRequestId,
        createdAt: createdAt?.toDate ? formatDate(createdAt.toDate(), 'yyyy/MM/dd HH:mm:ss') : '',
        pickedAt: pickedAt?.toDate ? formatDate(pickedAt.toDate(), 'yyyy/MM/dd HH:mm:ss') : '',
      };
    });
  };

  return (
    <div className="admin-locations container-fluid">
      <div className="p-4 bg-white my-4">
        <div className="d-flex justify-content-center mb-3">
          <h4>ロケーション管理</h4>
        </div>
        <div className="d-flex justify-content-between align-items-end mb-3 gap-1">
          <QueryInput paramName="key" label="ロケキー（前方一致）" width={250} />
          <div className="d-flex gap-1">
            <ExportButton fileName="ロケーション.csv" rows={rowsForExport} label="一覧CSV" />
            <ExportButton fileName={`ロケ別在庫_${formatDate(new Date(), 'yyyyMMdd_HHmm')}.csv`} rows={rowsForInventoryCsv} label="在庫CSV" />
            <Button color="primary" onClick={_ => toggleBatchAddModal(true)}>
              <span className="fas fa-plus mr-1" />一括追加
            </Button>
            <Button color="primary" outline onClick={_ => toggleBatchEditModal(true)}>
              <span className="fas fa-file-csv mr-1" />CSV一括編集
            </Button>
            <AddButton itemRef={locationsRef.doc()} FormModal={ModelFormModal} formProps={{ title: 'ロケーション追加', fields: fields({ otherLocations: locations }), }} />
          </div>
        </div>
        <div className="d-flex justify-content-between align-items-center mb-1">
          <div className="text-muted small">
            {totalCount != null ? `全 ${totalCount.toLocaleString()} 件` : '件数取得中…'}
          </div>
          <CursorPagination page={cursors.length + 1} hasPrev={hasPrev} hasNext={hasNext} onFirst={onFirst} onPrev={onPrev} onNext={onNext} />
        </div>
        <div>
          {
            locations.length > 0 ? (
              <table className="table">
                <thead className="thead-light text-center">
                  <tr>
                    <th>ID</th>
                    <th>キー</th>
                    <th>名称</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {
                    locations.map((location) => {
                      const { id, ref, sortKey, name } = location;
                      const beforeDelete = async () => {
                        if (
                          (await inventoriesRef.where('locationId', '==', id).limit(1).get()).docs.length > 0
                        ) {
                          toast.error('使用されているため削除できません');
                          return false;
                        }
                      };
                      const onClickPrintBarcode = async () => {
                        const labelWidth = 52.5;
                        const labelHeight = 29.7;
                        const scale = 10;
                        const canvas = document.createElement('canvas');
                        canvas.width = labelWidth * scale;
                        canvas.height = labelHeight * scale;
                        jsbarcode(canvas, sortKey, { width: 0.1 * scale, });
                        const imageData = canvas.toDataURL('image/jpeg');
                        const doc = new jsPDF({
                          orientation: 'landscape',
                          format: [labelWidth, labelHeight]
                        });

                        doc.addImage(imageData, 'JPEG', 0, 0, labelWidth, labelHeight);
                        const pdfData = doc.output('datauri');

                        await printBarcode({
                          type: 'location',
                          title: `location ${id}`,
                          imageData: pdfData,
                        });
                      };

                      return (
                        <tr key={id}>
                          <td>{id}</td>
                          <td>{sortKey}</td>
                          <td>{name}</td>
                          <td className="text-right">
                            <Button onClick={onClickPrintBarcode}>ラベル発行</Button>
                            <InventoryButton className="ml-2" location={location} />
                            <EditButton
                              itemRef={ref}
                              className="ml-2"
                              FormModal={ModelFormModal}
                              formProps={{ title: 'ロケーション編集', fields: fields(), }}
                              beforeUpdate={async ({ sortKey: newSortKey }) => {
                                if (newSortKey === sortKey) return;
                                const { docs } = await inventoriesRef.where('locationId', '==', id).get();
                                if (docs.length > 0 && !window.confirm(`ロケキーを「${sortKey}」→「${newSortKey}」に変更します。\nこのロケに紐づく在庫 ${docs.length} 件のロケキーも書き換えます。よろしいですか？`)) {
                                  return false;
                                }
                              }}
                              onFinish={async ({ sortKey: newSortKey }) => {
                                if (newSortKey === sortKey) return;
                                const { docs } = await inventoriesRef.where('locationId', '==', id).get();
                                if (docs.length === 0) return;
                                await batch(db, docs, (b, _) => b.update(_.ref, { locationKey: newSortKey }));
                                toast.success(`在庫 ${docs.length} 件のロケキーを更新しました`);
                              }}
                            />
                            <DeleteButton itemRef={ref} className="ml-2" beforeDelete={beforeDelete} />
                          </td>
                        </tr>
                      );
                    })
                  }
                </tbody>
              </table>
            ) : (
              <div>
                {isLoading ? <span className="fas fa-spin fa-spinner" /> : <span>No Data</span>}
              </div>
            )
          }
        </div>
        {
          showsBatchAddModal && (
            <LocationsBatchAddModal
              isOpen
              onClickClose={_ => toggleBatchAddModal(false)}
              onFinish={onFirst}
            />
          )
        }
        {
          showsBatchEditModal && (
            <LocationsBatchEditModal
              isOpen
              onClickClose={_ => toggleBatchEditModal(false)}
              onFinish={onFirst}
            />
          )
        }
      </div>
    </div>
  );
});

function InventoryButton(props) {
  const { location, ...extraProps } = props;
  const [showsModal, toggleModal] = useToggle(false);
  const inventories = useCollectionSubscription(showsModal && inventoriesRef.where('locationId', '==', location.id).where('status', '==', 'shelved'), [showsModal, location]);
  const inventoriesGroupedByProductId = groupBy(inventories, _ => [_.tenantId, _.productId].join('-'));
  const sortedProductIds = sortBy(keys(inventoriesGroupedByProductId), _ => parseInt(_.match(new RegExp(`${env('TENANT_PREFIX')}(\\d+)`))[1], 10), _ => parseInt(_.match(/(\d+)$/), 10));

  return (
    <Fragment>
      <Button onClick={toggleModal.bind(null, true)} {...extraProps}>
        在庫確認
      </Button>
      {
        showsModal && (
          <Modal isOpen>
            <ModalHeader>
              {location.name} [{location.id}] 在庫確認
            </ModalHeader>
            <ModalBody>
              <table className="table table-bordered">
                <thead className="thead-light">
                  <tr>
                    <th>商品ID</th>
                    <th>数量</th>
                  </tr>
                </thead>
                <tbody>
                  {
                    sortedProductIds.map((productId) => {
                      const inventories = inventoriesGroupedByProductId[productId];
                      return (
                        <tr key={productId}>
                          <td>{productId}</td>
                          <td className="text-right">{inventories.length}</td>
                        </tr>
                      );
                    })
                  }
                </tbody>
              </table>
            </ModalBody>
            <ModalFooter>
              <Button className="cancel" color="secondary" onClick={toggleModal.bind(null, false)}>閉じる</Button>
            </ModalFooter>
          </Modal>
        )
      }
    </Fragment>
  );
}
